import { useEffect, useState } from 'react';
import type { ApiClient } from '../../api';
import type { MsrDokument, MsrDokumentart } from './typen';
import { ErrorState } from '../../muster';

const text = (e: unknown) => (e instanceof Error ? e.message : String(e));

/**
 * Der PDF-Knopf an ASP-Detail, Anlagendetail, Datenpunktliste, Einstellwerte
 * und Schemen (Spezifikation 15.5) — lädt das vorhandene Dokument der
 * passenden Art, oder bietet die Erzeugung an, wenn keines da ist.
 *
 * Ein Ziel, eine Art: `msr_dokumente` trägt UNIQUE (ProjectId, Art, ZielId),
 * es kann also höchstens ein Treffer zurückkommen.
 *
 * TM-8c: Fehler stehen als `ErrorState klein` mit dem Schritt, der scheiterte (Suche, Anzeigen, Herunterladen,
 * Erzeugen); eine gescheiterte Suche lässt sich wiederholen.
 */
export function PdfKnopf({
  api, projektId, art, ziel, zielId, label,
}: {
  api: ApiClient;
  projektId: string;
  art: MsrDokumentart;
  ziel: 'anlage' | 'asp' | 'projekt';
  /** Fehlt bei `ziel === 'projekt'`. */
  zielId?: string;
  /** Beschriftung des Erzeugen-Knopfs, Standard "PDF". */
  label?: string;
}) {
  const [dokument, setDokument] = useState<MsrDokument | null | undefined>(undefined);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<{ titel: string; text: string; suche?: boolean } | null>(null);

  const neuLaden = () => {
    setFehler(null);
    api.msrDokumente(projektId, {
      art,
      anlage: ziel === 'anlage' ? zielId : undefined,
      asp: ziel === 'asp' ? zielId : undefined,
      ziel,
    }).then((zeilen) => setDokument(zeilen[0] ?? null))
      .catch((e) => { setDokument(null); setFehler({ titel: 'Die Dokumentsuche ist gescheitert.', text: text(e), suche: true }); });
  };

  useEffect(neuLaden, [api, projektId, art, ziel, zielId]);

  const anzeigen = async () => {
    if (!dokument) return;
    try {
      const url = await api.msrDokumentAnzeigen(projektId, dokument.id);
      window.open(url, '_blank');
    } catch (e) {
      setFehler({ titel: 'Das PDF konnte nicht geöffnet werden.', text: text(e) });
    }
  };

  const herunterladen = async () => {
    if (!dokument) return;
    try {
      await api.msrDokumentHerunterladen(projektId, dokument.id);
    } catch (e) {
      setFehler({ titel: 'Das PDF konnte nicht heruntergeladen werden.', text: text(e) });
    }
  };

  const erzeugen = async () => {
    setLaeuft(true);
    setFehler(null);
    try {
      await api.msrDokumenteErzeugen(projektId, {
        arten: [art],
        anlage: ziel === 'anlage' ? zielId : undefined,
        asp: ziel === 'asp' ? zielId : undefined,
      });
      neuLaden();
    } catch (e) {
      setFehler({ titel: 'Das PDF konnte nicht erzeugt werden.', text: text(e) });
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
      {dokument === undefined && <span className="schwach" style={{ fontSize: 11 }}>PDF …</span>}

      {dokument && (
        <>
          <button type="button" className="m-knopf" onClick={anzeigen}>Anzeigen</button>
          <button type="button" className="m-knopf" onClick={herunterladen}>Herunterladen</button>
        </>
      )}

      {dokument === null && (
        <button type="button" className="m-knopf" disabled={laeuft || !projektId} onClick={erzeugen}>
          {laeuft ? 'läuft …' : `${label ?? 'PDF'} erzeugen`}
        </button>
      )}

      {fehler && (
        <ErrorState klein titel={fehler.titel} technisch={fehler.text}
          beiErneutVersuchen={fehler.suche ? neuLaden : undefined} />
      )}
    </span>
  );
}
